// ---------------------------------------------------------------------------
// useEvaluationTemplates — fetch the list of evaluation templates for the
// supplied workspace. When `workspaceName` is null the hook stays idle and
// returns an empty, non-loading result so the consumer can render a
// "pick a workspace" placeholder without a network call.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { EvaluationTemplatesService } from '../../services/evaluationTemplatesService.js';
import type { EvaluationTemplate } from '../../types/evaluationTemplate.js';

import type {
  UseEvaluationTemplatesParams,
  UseEvaluationTemplatesResult,
} from './useEvaluationTemplates.interface.js';

export function useEvaluationTemplates(
  params: UseEvaluationTemplatesParams,
): UseEvaluationTemplatesResult {
  const { workspaceName } = params;
  const client = useHttpClient();
  const service = useMemo(
    () => new EvaluationTemplatesService(client),
    [client],
  );

  const [fetchedTemplates, setFetchedTemplates] = useState<
    EvaluationTemplate[]
  >([]);
  const [pendingUpserts, setPendingUpserts] = useState<
    Record<string, EvaluationTemplate>
  >({});
  const [pendingRemovals, setPendingRemovals] = useState<Set<string>>(
    () => new Set(),
  );

  const templates = useMemo(() => {
    const upserts = Object.values(pendingUpserts).filter(
      (template) => !pendingRemovals.has(template.name),
    );
    const fetched = fetchedTemplates.filter(
      (template) =>
        !pendingRemovals.has(template.name) &&
        pendingUpserts[template.name] === undefined,
    );

    return [...upserts, ...fetched];
  }, [fetchedTemplates, pendingRemovals, pendingUpserts]);

  const [isLoading, setIsLoading] = useState(workspaceName !== null);
  const [error, setError] = useState<string | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);

  const refresh = useCallback(() => {
    setRefreshCount((n) => n + 1);
  }, []);

  const upsertTemplate = useCallback((template: EvaluationTemplate) => {
    setPendingUpserts((previous) => ({
      ...previous,
      [template.name]: template,
    }));
    setPendingRemovals((previous) => {
      if (!previous.has(template.name)) return previous;
      const next = new Set(previous);
      next.delete(template.name);
      return next;
    });
  }, []);

  // Optimistically drop a template from the list (e.g. after a delete) so the
  // rail updates immediately without waiting for a refetch.
  const removeTemplate = useCallback((name: string) => {
    setPendingRemovals((previous) => {
      if (previous.has(name)) return previous;
      const next = new Set(previous);
      next.add(name);
      return next;
    });
    setPendingUpserts((previous) => {
      if (previous[name] === undefined) return previous;
      const { [name]: _removed, ...rest } = previous;
      return rest;
    });
  }, []);

  useEffect(() => {
    setFetchedTemplates([]);
    setPendingUpserts({});
    setPendingRemovals(new Set());
  }, [workspaceName]);

  useEffect(() => {
    if (workspaceName === null) {
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    service
      .list(workspaceName)
      .then((items) => {
        if (cancelled) return;
        setFetchedTemplates(items);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFetchedTemplates([]);
        setError(
          err instanceof Error ? err.message : 'Failed to load eval templates',
        );
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [service, workspaceName, refreshCount]);

  return {
    templates,
    isLoading,
    error,
    refresh,
    upsertTemplate,
    removeTemplate,
  };
}
